import { CheckIcon, MinusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import PausedChip from '@/components/lock/PausedChip';
import { getGroupColor } from './groupColors';
import type { HiddenCalendars, SidebarOrg } from './useCalendarEvents';

type CheckState = 'checked' | 'unchecked' | 'mixed';

function CheckBox({
  state,
  colorClass,
  onClick,
  label,
}: {
  state: CheckState;
  /** Solid bg class when checked (group color, or navy for org/all rows). */
  colorClass: string;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      role="checkbox"
      aria-checked={state === 'mixed' ? 'mixed' : state === 'checked'}
      aria-label={label}
      className={clsx(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition',
        state === 'unchecked'
          ? 'border-flock-gray-300 bg-white hover:border-flock-navy-400'
          : clsx(colorClass, 'border-transparent text-white')
      )}
    >
      {state === 'checked' && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
      {state === 'mixed' && <MinusIcon className="h-3 w-3" strokeWidth={3} />}
    </button>
  );
}

interface Props {
  orgs: SidebarOrg[];
  hidden: HiddenCalendars;
  onChange: (next: HiddenCalendars) => void;
}

/**
 * The "My calendars" rail: select/deselect everything, a whole organization,
 * or individual groups (Google-Calendar-style colored checkboxes).
 */
export default function CalendarSidebar({ orgs, hidden, onChange }: Props) {
  const orgState = (org: SidebarOrg): CheckState => {
    if (hidden.orgIds.has(org.id)) return 'unchecked';
    const hiddenCount = org.groups.filter((g) => hidden.groupIds.has(g.id)).length;
    if (hiddenCount === 0) return 'checked';
    if (hiddenCount === org.groups.length && org.groups.length > 0) return 'unchecked';
    return 'mixed';
  };

  const allState: CheckState = (() => {
    const states = orgs.map(orgState);
    if (states.every((s) => s === 'checked')) return 'checked';
    if (states.every((s) => s === 'unchecked')) return 'unchecked';
    return 'mixed';
  })();

  const toggleAll = () => {
    if (allState === 'checked') {
      onChange({ orgIds: new Set(orgs.map((o) => o.id)), groupIds: new Set() });
    } else {
      onChange({ orgIds: new Set(), groupIds: new Set() });
    }
  };

  const toggleOrg = (org: SidebarOrg) => {
    const orgIds = new Set(hidden.orgIds);
    const groupIds = new Set(hidden.groupIds);
    // Clear any per-group state for this org either way
    for (const g of org.groups) groupIds.delete(g.id);
    if (orgState(org) === 'unchecked') orgIds.delete(org.id);
    else orgIds.add(org.id);
    onChange({ orgIds, groupIds });
  };

  const toggleGroup = (org: SidebarOrg, groupId: string) => {
    const orgIds = new Set(hidden.orgIds);
    const groupIds = new Set(hidden.groupIds);
    if (orgIds.has(org.id)) {
      // Org was fully hidden — show just this group
      orgIds.delete(org.id);
      for (const g of org.groups) if (g.id !== groupId) groupIds.add(g.id);
    } else if (groupIds.has(groupId)) {
      groupIds.delete(groupId);
    } else {
      groupIds.add(groupId);
    }
    onChange({ orgIds, groupIds });
  };

  return (
    <aside className="flex w-60 shrink-0 flex-col overflow-y-auto border-r border-flock-gray-200 bg-white px-4 py-4">
      <div className="flex items-center gap-2.5">
        <CheckBox
          state={allState}
          colorClass="bg-flock-navy-600"
          onClick={toggleAll}
          label="All calendars"
        />
        <button
          onClick={toggleAll}
          className="text-sm font-semibold text-flock-navy-900 hover:text-flock-navy-700"
        >
          All calendars
        </button>
      </div>

      <div className="mt-4 space-y-4">
        {orgs.map((org) => (
          <section key={org.id}>
            <div className="flex items-center gap-2.5">
              <CheckBox
                state={orgState(org)}
                colorClass="bg-flock-navy-600"
                onClick={() => toggleOrg(org)}
                label={org.name}
              />
              <button
                onClick={() => toggleOrg(org)}
                className="min-w-0 flex-1 truncate text-left text-xs font-semibold uppercase tracking-wide text-flock-navy-500 hover:text-flock-navy-700"
                title={org.name}
              >
                {org.name}
              </button>
              {org.locked && <PausedChip />}
            </div>
            <ul className="mt-1.5 space-y-0.5">
              {org.groups.map((group) => {
                const color = getGroupColor(group.id);
                const visible = !hidden.orgIds.has(org.id) && !hidden.groupIds.has(group.id);
                return (
                  <li key={group.id}>
                    <div
                      className={clsx(
                        'flex items-center gap-2.5 rounded-lg py-1.5 pl-6 pr-2 transition hover:bg-flock-navy-50/70',
                        !visible && 'opacity-60'
                      )}
                    >
                      <CheckBox
                        state={visible ? 'checked' : 'unchecked'}
                        colorClass={color.dot}
                        onClick={() => toggleGroup(org, group.id)}
                        label={group.name}
                      />
                      <button
                        onClick={() => toggleGroup(org, group.id)}
                        className="min-w-0 flex-1 truncate text-left text-sm text-flock-navy-700"
                        title={group.name}
                      >
                        {group.name}
                      </button>
                    </div>
                  </li>
                );
              })}
              {org.groups.length === 0 && (
                <li className="pl-6 text-xs text-flock-navy-300">No groups yet</li>
              )}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}
